// askSmart-ui/src/pages/TOSP/components/TaxRollModal.tsx
import React, { useEffect, useState } from "react";
import {
  newId,
  nextPartLabel,
  partShares,
  TaxRollAllocation,
  TaxRollPart,
  TaxRollRecord,
  Unit,
} from "../types";
import { requiredText, sameText } from "../validation";

interface TaxRollModalProps {
  isOpen: boolean;
  /** The roll being edited, or null when adding a new one. */
  roll: TaxRollRecord | null;
  /** The whole register, so the roll number can be checked for duplicates. */
  rolls: TaxRollRecord[];
  /** Areas drive the split, and names show which unit holds which part. */
  units: Unit[];
  onClose: () => void;
  /** Identity and division only — money belongs to the year tabs. Awaited, so
   *  the button can hold until the write is done. */
  onSave: (values: Omit<TaxRollRecord, "id" | "levies" | "budgets">) => void | Promise<void>;
}

const FIELDS: {
  key: "rollNumber" | "planNumber" | "level" | "unit" | "municipalAddress";
  label: string;
  required?: boolean;
  hint?: string;
}[] = [
  {
    key: "rollNumber",
    label: "Roll #",
    required: true,
    hint: "As the municipality writes it, e.g. 000-020-00440-0000",
  },
  { key: "planNumber", label: "Plan #", hint: "Digits only, e.g. 61" },
  { key: "level", label: "Level", hint: "1, A, B…" },
  { key: "unit", label: "Unit" },
  { key: "municipalAddress", label: "Municipal address" },
];

// Add or edit one roll by hand.
//
// The register starts from the municipality's bill, but it has to be able to
// change between bills: a new store gets its own roll, and splitting a store in
// two replaces one roll with two. That's what this is for — everything else
// about a roll arrives on the next bill.
export default function TaxRollModal({
  isOpen,
  roll,
  rolls,
  units,
  onClose,
  onSave,
}: TaxRollModalProps) {
  const [values, setValues] = useState({
    rollNumber: "",
    planNumber: "",
    level: "",
    unit: "",
    municipalAddress: "",
    legalDescription: "",
  });
  // Every allocation the roll has ever had. Only the newest is editable here;
  // the earlier ones belong to years that have already been settled.
  const [allocations, setAllocations] = useState<TaxRollAllocation[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  // Reloaded on open, so a cancelled edit leaves nothing behind for the next Add.
  useEffect(() => {
    if (!isOpen) return;
    setValues({
      rollNumber: roll?.rollNumber || "",
      planNumber: roll?.planNumber || "",
      level: roll?.level || "",
      unit: roll?.unit || "",
      municipalAddress: roll?.municipalAddress || "",
      legalDescription: roll?.legalDescription || "",
    });
    setAllocations(
      roll?.allocations?.length
        ? // Drop earlier divisions nobody was ever assigned under. They're not
          // a record of how a year divided, and while they sit in front of the
          // real one they make that year read as unallocated. The newest is
          // always kept, assigned or not.
          roll.allocations.filter(
            (allocation, index) =>
              index === roll.allocations.length - 1 ||
              allocation.parts.some((part) => part.assignedTo),
          )
        : [
            {
              id: newId("alloc"),
              fromYear: 0,
              parts: [{ id: newId("part"), label: "", assignedTo: null }],
            },
          ],
    );
    setErrors({});
    setBusy(false);
  }, [isOpen, roll]);

  // Allocations are loaded by the effect above, which runs after the first
  // render — so hold the frame until they're there rather than reading past
  // the end of an empty array.
  if (!isOpen || allocations.length === 0) return null;

  const current = allocations[allocations.length - 1];

  /**
   * Edits the division in place, so it applies to every year on record.
   *
   * The model can date an allocation, and `allocationFor` will honour it — but
   * nothing creates a dated one, because there's no settled year to protect.
   * The first bill loaded is 2025 and no earlier year carries a budget or an
   * allocation, so a split made today is simply how these rolls divide. Dating
   * it from this year would leave 2025 pointing at a division that never
   * existed.
   *
   * When a year has been budgeted and reconciled, this is where forking would
   * go back in: keep the settled year's allocation and add a new one from the
   * first open year.
   */
  const editCurrent = (parts: TaxRollPart[]) =>
    setAllocations((prev) => [...prev.slice(0, -1), { ...prev[prev.length - 1], parts }]);

  const split = () => {
    const parts = current.parts;
    // The first split names the existing whole "A" so both halves read alike.
    const relabelled =
      parts.length === 1 && !parts[0].label ? [{ ...parts[0], label: "A" }] : parts;
    editCurrent([
      ...relabelled,
      { id: newId("part"), label: nextPartLabel(relabelled), assignedTo: null },
    ]);
  };

  // "A"/"B" is only a starting point — a part can be named whatever the suite
  // is actually called, so the picker on the unit form reads the way staff
  // speak about it.
  const renamePart = (partId: string, label: string) =>
    editCurrent(
      current.parts.map((part) => (part.id === partId ? { ...part, label } : part)),
    );

  const removePart = (partId: string) => {
    const remaining = current.parts.filter((part) => part.id !== partId);
    // Back to one part means the roll is whole again, so the label goes too.
    editCurrent(
      remaining.length === 1 ? [{ ...remaining[0], label: "" }] : remaining,
    );
  };

  const unitName = (part: TaxRollPart): string => {
    if (part.assignedTo?.type !== "unit") return "";
    const unit = units.find((u) => u.id === part.assignedTo?.id);
    return unit ? `Suite ${unit.suiteNumber}` : "Missing unit";
  };

  const shares = partShares(current, units);
  const shareOf = (part: TaxRollPart): string => {
    const found = shares?.find((s) => s.part.id === part.id);
    return found ? `${(found.share * 100).toFixed(1)}%` : "—";
  };

  // Names are what the unit form's picker shows, so two parts reading alike
  // would leave no way to tell which half is being assigned.
  const labels = current.parts.map((part) => part.label.trim().toLowerCase());
  const partsError =
    current.parts.length > 1
      ? labels.some((label) => !label)
        ? "Every part needs a name."
        : new Set(labels).size < labels.length
          ? "Two parts can't share a name."
          : ""
      : "";

  const set = (key: keyof typeof values, value: string) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    const next: Record<string, string> = {};

    const rollError = requiredText(values.rollNumber, "Roll #");
    if (rollError) {
      next.rollNumber = rollError;
    } else if (
      rolls.some((other) => other.id !== roll?.id && sameText(other.rollNumber, values.rollNumber))
    ) {
      // The roll number is the key every future bill matches on — two records
      // sharing one would make the match ambiguous.
      next.rollNumber = "That roll number is already in the register.";
    }

    if (partsError) next.parts = partsError;

    setErrors(next);
    if (Object.keys(next).length > 0) return;

    const plan = values.planNumber.trim();
    const level = values.level.trim();
    const unit = values.unit.trim();

    setBusy(true);
    try {
      await onSave({
        rollNumber: values.rollNumber.trim(),
        planNumber: plan,
        level,
        unit,
        municipalAddress: values.municipalAddress.trim(),
        // Written from the parts when it wasn't given, so the description
        // always agrees with the fields beside it.
        legalDescription:
          values.legalDescription.trim() ||
          (plan && level && unit ? `GSCP ${plan} LEVEL ${level} UNIT ${unit}` : ""),
        // A hand-added roll hasn't been billed yet; the next bill fills it in.
        lastBilled: roll?.lastBilled || "",
        allocations: allocations.map((allocation) =>
          allocation === current
            ? {
                ...allocation,
                parts: allocation.parts.map((part) => ({ ...part, label: part.label.trim() })),
              }
            : allocation,
        ),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="smrtdocs-modal-overlay">
      <div className="smrtdocs-modal">
        <div className="smrtdocs-modal-header">
          <h2 style={{ margin: 0, fontSize: "1rem" }}>
            {roll ? "Edit Tax Roll" : "Add Tax Roll"}
          </h2>
        </div>
        <form className="smrtdocs-modal-body" onSubmit={handleSubmit} noValidate autoComplete="off">
          {FIELDS.map((field) => (
            <React.Fragment key={field.key}>
              <div className="tosp-field">
                <label className="tosp-label" htmlFor={`tosp-roll-${field.key}`}>
                  {field.label}
                  {field.required && <span className="tosp-required"> *</span>}
                </label>
                <input
                  id={`tosp-roll-${field.key}`}
                  className="manage-input"
                  autoComplete="off"
                  value={values[field.key]}
                  onChange={(e) => set(field.key, e.target.value)}
                />
                {errors[field.key] ? (
                  <p className="tosp-field-error">{errors[field.key]}</p>
                ) : (
                  field.hint && <p className="tosp-field-hint">{field.hint}</p>
                )}
              </div>

              {/* Splitting sits directly under Roll #, because it's a
                  statement about that one parcel: the municipality still bills
                  it whole, and this is only how the cost divides internally. */}
              {field.key === "rollNumber" && (
                <div className="tosp-split">
                  <div className="tosp-split-head">
                    <span className="tosp-label" style={{ margin: 0 }}>
                      {current.parts.length > 1
                        ? `Split ${current.parts.length} ways`
                        : "Not split"}
                    </span>
                    <button type="button" className="tosp-btn-blue" onClick={split}>
                      Split
                    </button>
                  </div>

                  {current.parts.length > 1 && (
                    <>
                      <div className="tosp-split-list">
                        {current.parts.map((part) => (
                          <div key={part.id} className="tosp-split-row">
                            <span className="tosp-split-name">
                              {values.rollNumber || "—"} (
                              <input
                                className="tosp-split-label"
                                aria-label="Part name"
                                autoComplete="off"
                                maxLength={16}
                                size={Math.max(part.label.length, 1)}
                                value={part.label}
                                onChange={(e) => renamePart(part.id, e.target.value)}
                              />
                              )
                            </span>
                            <span className="tosp-split-where">
                              {unitName(part) || (
                                <span className="tosp-roll-unassigned">Unassigned</span>
                              )}
                            </span>
                            <span className="tosp-split-share">{shareOf(part)}</span>
                            <button
                              type="button"
                              className="tosp-split-remove"
                              aria-label={`Remove part ${part.label}`}
                              title="Remove this part"
                              onClick={() => removePart(part.id)}
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                      {errors.parts ? (
                        <p className="tosp-field-error">{errors.parts}</p>
                      ) : (
                        <p className="tosp-field-hint">
                          Names are yours to change. Each part is assigned on its unit's
                          own form, and the shares come from those units' Occupancy SqFt.
                          {!shares && " Both parts need a unit before any cost can be divided."}
                        </p>
                      )}
                    </>
                  )}
                </div>
              )}
            </React.Fragment>
          ))}

          <div className="tosp-field">
            <label className="tosp-label" htmlFor="tosp-roll-legal">
              Legal description
            </label>
            <input
              id="tosp-roll-legal"
              className="manage-input"
              autoComplete="off"
              placeholder="Written from Plan / Level / Unit when left blank"
              value={values.legalDescription}
              onChange={(e) => set("legalDescription", e.target.value)}
            />
          </div>

          {roll && current.parts.length === 1 && (
            <div className="tosp-detail-row">
              <div className="tosp-detail-label">Assigned to</div>
              <div className="tosp-detail-value">
                {unitName(current.parts[0]) || "— not assigned to a unit yet"}
              </div>
            </div>
          )}

          {/* Deleting is done from the register's row action, the same as
              every other TOSP grid. */}
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <button type="button" className="btn-cancel" disabled={busy} onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-save" disabled={busy}>
              {busy ? (
                <>
                  <span className="tosp-spinner" aria-hidden="true" />
                  Saving…
                </>
              ) : (
                "Save"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
